import { useLanguage } from "../../context/LanguageContext";
import styles from "./ModulePage.module.css";
import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { deleteUser, postUser } from "../../helper/http.ts";
import TablePage from "../TablePage/TablePage.tsx";
import { getValidationRulesForModule } from "../../config/validationRules.ts";
import { parseValidationRules } from "../../utils/parseValidationRules.ts";
import {IReportAction, TableRow} from "../../types/types.table.ts";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useIconPack} from "../../context/IconPackContext.tsx";
import ReportListView from "../../components/ReportListView/ReportListView.tsx";
import ReportItemView from "../../components/ReportItemView/ReportItemView.tsx";
import ReportView from "../../components/ReportView/ReportView.tsx";
import ReportSource from "../../components/ReportSource/ReportSource.tsx";

export default function ModulePage(props: { isReportMode?: boolean }) {
    const { language, t } = useLanguage();
    const { id } = useParams();
    const {getIcon} = useIconPack();

    const [module, setModule] = useState<any>();
    const [fields, setFields] = useState<string[]>([]);
    const [sortField, setSortField] = useState<string>('id');
    const [moduleFields, setModuleFields] = useState<any[]>([]);
    const [maps, setMaps] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(true);
    const [reportActions, setActions] = useState([]);
    const fetchedRef = useRef(false);

    const [slides, setSlides] = useState<({action: IReportAction, row: TableRow, result: string} | null)[]>([null, null, null, null]);
    const [slideStart, setSlideStart] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [expandedSlide, setExpandedSlide] = useState<{action: IReportAction, row: TableRow} | null>(null);
    const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
    const SLIDES_VISIBLE = 4;

    const activeSlides = slides.filter(slide => slide !== null);
    const totalSlides = activeSlides.length;

    const scrollToSlide = (index: number) => {
        const slideElement = slideRefs.current[index];
        if (slideElement) {
            slideElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    const goToNextSlide = () => {
        const newIndex = Math.min(currentSlideIndex + 1, totalSlides - 1);
        setCurrentSlideIndex(newIndex);
        
        if (newIndex >= slideStart + SLIDES_VISIBLE) {
            setSlideStart(newIndex - SLIDES_VISIBLE + 1);
        }
        
        setTimeout(() => scrollToSlide(newIndex), 100);
    };

    const goToPrevSlide = () => {
        const newIndex = Math.max(currentSlideIndex - 1, 0);
        setCurrentSlideIndex(newIndex);
        
        if (newIndex < slideStart) {
            setSlideStart(newIndex);
        }
        
        setTimeout(() => scrollToSlide(newIndex), 100);
    };

    const buildValidationRules = (fieldData: any, tableName: string) => {
        const configRules = getValidationRulesForModule(
            tableName,
            fieldData.name,
            fieldData.file_type
        );

        if (configRules) return configRules;

        if (fieldData.validation) {
            return parseValidationRules(fieldData.validation);
        }

        return undefined;
    };

    const reportActionCallBack = (action: IReportAction, row: TableRow) => {
        if (action.target === null) return;



        let targetId = 0;
        if (action.target === 'new') {
            let fndIndex = slides.findIndex(slide => slide === null);

            if (fndIndex >= 0) {
                slides[fndIndex] = {action, row, result: "..."};
            } else {
                slides.push({action, row, result: "..."});

                if (slides.length > 4) {
                    setSlideStart(slideStart + 1);
                }
            }

            setSlides([...slides]);
            
            const newActiveSlides = slides.filter(s => s !== null);
            const newIndex = newActiveSlides.length - 1;
            setCurrentSlideIndex(newIndex);
            
            if (newIndex >= slideStart + SLIDES_VISIBLE) {
                setSlideStart(newIndex - SLIDES_VISIBLE + 1);
            }
            
            setTimeout(() => scrollToSlide(newIndex), 100);
            targetId = newIndex;
        } else {
            targetId = +action.target;
            slides[targetId] = {action, row, result: "..."};
            setSlides([...slides]);
        }


        if (action.type === 'source_start' || action.type === 'source_stop') {

            postUser(`report/${action.type}`, {
                sourceId: row.id.toString(),
            }).then((res) => {
                if (slides[targetId]) {
                    (slides[targetId] as {result: string}).result = res.result;
                    setSlides([...slides]);
                }
            });
        }

    }

    const closeSlide = (id: number) => {
        console.log(id, slideStart);
        if (id > 3) {
            slides.splice(id, 1);
            setSlides([...slides]);
            if (slideStart && slideStart + 4 >= slides.length) {
                setSlideStart(slideStart - 1);
            }
        } else {
            slides[id] = null;
            setSlides([...slides]);
        }
        
        const newActiveSlides = slides.filter(s => s !== null);
        if (currentSlideIndex >= newActiveSlides.length && newActiveSlides.length > 0) {
            setCurrentSlideIndex(newActiveSlides.length - 1);
        } else if (newActiveSlides.length === 0) {
            setCurrentSlideIndex(0);
        }
    }

    useEffect(() => {
        fetchedRef.current = false;

        if (fetchedRef.current) return;

        if (props.isReportMode) {
            postUser("crud/list", {
                module: "report",
                page: "1",
                per_page: "100",
                sort_direction: "+",
                sort_field: "id",
            }).then((reportRes) => {
                const foundReport = reportRes.data.find((t: any) => String(t.id) === String(id));
                const fields = JSON.parse(foundReport.table_fields);
                const actions = JSON.parse(foundReport.right_plugin);

                setActions(actions);
                setModule(foundReport);

                setModuleFields(fields.map((item: any) => ({
                    ...item,
                    name: item.field,
                    validation: buildValidationRules(item, foundReport.table_name)
                })));

                setLoading(false);
            });

            return;
        }

        fetchedRef.current = true;

        let isMounted = true;
        setLoading(true);

        Promise.all([
            postUser('crud/list', {
                "filters": {
                    id: +(id || 0)
                },
                "module": "module",
                "page": "1",
                "per_page": "100",
                "sort_direction": "+",
                "sort_field": "order"
            }),
            postUser('crud/list', {
                "filters": {
                    module_id: +(id || 0)
                },
                "module": "module_field",
                "page": "1",
                "per_page": "100",
                "sort_direction": "+",
                "sort_field": "order"
            })
        ]).then(([moduleRes, fieldsRes]) => {
            if (!isMounted) return;

            const moduleData = moduleRes.data[0];
            setModule(moduleData);

            const parsedFields = fieldsRes.data.map((field: any) => ({
                ...field,
                validation: buildValidationRules(field, moduleData.table_name)
            }));

            setModuleFields(parsedFields);
            setFields(fieldsRes.data.map((field: any) => field.name));
            setLoading(false);
        });

        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (module && moduleFields.length) {
            const defaultSortFiled = moduleFields.find((field: any) => field.id === module.sort_field_id);
            if (defaultSortFiled) {
                setSortField(defaultSortFiled.name);
            } else {
                setSortField('id');
            }
        }
    }, [module, moduleFields]);

    return (
        <div className={styles.container + (slides.find(slide => slide != null) ? ' ' + styles.leftSideCnt : '') + (expandedSlide ? ' ' + styles.hasExpandedSlide : '')}>
            {(module && moduleFields.length > 0) && (
                <TablePage
                    key={module.id}
                    title={module.name}
                    module={module}
                    tableName={module.table_name}
                    fields={moduleFields}
                    maps={maps}
                    reportActions={reportActions}
                    reportActionCallBack={reportActionCallBack}
                    isReportMode={!!props.isReportMode}
                    sortStateField={sortField}
                    template={module.template_type || "default"}
                    leftContent={module.template_type === "left" ? <div>Left panel{module.name}</div> : undefined}
                    rightContent={module.template_type === "right" ? <div>Right panel {module.name}</div> : undefined}
                    deleteData={async (params) => {
                        return deleteUser("crud/delete", {
                            id: +params.id,
                            module: module.table_name,
                        });
                    }}
                    createData={async (params) => {
                        const res = await postUser("crud/upsert", {
                            body: params,
                            module: module.table_name,
                        });
                        return res;
                    }}
                    saveData={async ({ id, ...params }) => {
                        const res = await postUser("crud/upsert", {
                            id: +id,
                            body: params,
                            module: module.table_name,
                        });
                        return res;
                    }}
                    loadData={async (params) => {
                        let searchParams: any = props.isReportMode ? {
                            report_id: module.id,
                            page: params.pagination.page.toString(),
                            per_page: params.pagination.limit.toString(),
                            sort_direction: params.sort.direction === "asc" ? "+" : "-",
                            sort_field: params.sort.column || sortField,
                        } : {
                            module: module.table_name,
                            page: params.pagination.page.toString(),
                            per_page: params.pagination.limit.toString(),
                            sort_direction: params.sort.direction === "asc" ? "+" : "-",
                            sort_field: params.sort.column || sortField,
                        };

                        let clearFilters = params.filters || {};
                        for (const key in clearFilters) {
                            if (!clearFilters[key]) delete clearFilters[key];
                        }

                        if (Object.keys(clearFilters).length) {
                            searchParams.filters = clearFilters;
                        }

                        const res = await postUser(props.isReportMode ? "report/list" : "crud/list", searchParams);

                        if (res.data && res.data.length > 0 && res.data[0].jsonf) {
                            console.log('res from back:', JSON.stringify(res.data[0], null, 2));
                            if (typeof res.data[0].jsonf === 'object') {
                                console.log('json field keys order:', Object.keys(res.data[0].jsonf));
                            }
                        }

                        const fks = moduleFields.filter(
                            (f: any) => f.file_type === "fk" && f.relation_module
                        );

                        let newMaps = { ...maps };

                        for (const fk of fks) {
                            const ids = res.data
                                .filter((row: any) => row[fk.name])
                                .map((row: any) => +row[fk.name]);

                            if (ids.length > 0) {
                                const { list } = await postUser("crud/select", {
                                    filter_ids: ids,
                                    module_id: +fk.relation_module,
                                });

                                if (!newMaps[fk.relation_module]) {
                                    newMaps[fk.relation_module] = {};
                                }
                                Object.assign(newMaps[fk.relation_module], list);
                            }
                        }

                        setMaps(newMaps);

                        return {
                            data: res.data,
                            total: res.total,
                        };
                    }}
                />
            )}

            {slides.find(slide => slide != null) && (
                <div className={styles.rightSide}>
                    {!expandedSlide ? (
                        <>
                            <div className={styles.slideContent}>
                                <div className={styles.slideGrid}>
                                    {slides.slice(slideStart, slideStart + 4).map((slide, index) => {
                                        if (slide == null) return null;
                                        
                                        const absoluteIndex = slideStart + index;
                                        const isActive = absoluteIndex === currentSlideIndex;
                                        
                                        return (
                                            <div 
                                                key={absoluteIndex}
                                                ref={(el) => {
                                                    slideRefs.current[absoluteIndex] = el;
                                                }}
                                                className={`${styles.slideGridItem} ${isActive ? styles.activeSlide : ''}`}
                                                onClick={() => {
                                                    setCurrentSlideIndex(absoluteIndex);
                                                    setExpandedSlide(slide);
                                                }}
                                            >
                                                <div className={styles.slideHead}>
                                                    <span className={styles.slideTitle}>{slide.action.type} {slide.row.date || slide.row.id}</span>
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            closeSlide(slideStart + index);
                                                        }} 
                                                        className={styles.slideBut}
                                                    >
                                                        <FontAwesomeIcon icon={getIcon("close")}/>
                                                    </button>
                                                </div>
                                                <div className={styles.slideBody}>
                                                    {slide.action.type === 'list' && <ReportListView action={slide.action} row={slide.row}/>}
                                                    {slide.action.type === 'view' && <ReportItemView action={slide.action} row={slide.row}/>}
                                                    {slide.action.type === 'report' && <ReportView action={slide.action} row={slide.row}/>}
                                                    {slide.action.type === 'source_start' && <ReportSource action={slide.action} row={slide.row} result={slide.result}/>}
                                                    {slide.action.type === 'source_stop' && <ReportSource action={slide.action} row={slide.row} result={slide.result}/>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {totalSlides > 1 && (
                                <div className={styles.slidePagination}>
                                    <button 
                                        className={styles.paginationButton}
                                        onClick={goToPrevSlide}
                                        disabled={currentSlideIndex === 0}
                                    >
                                        <FontAwesomeIcon icon={getIcon("arrow-left")}/>
                                    </button>
                                    
                                    <span className={styles.paginationInfo}>
                                        {currentSlideIndex + 1} / {totalSlides}
                                    </span>
                                    
                                    <button 
                                        className={styles.paginationButton}
                                        onClick={goToNextSlide}
                                        disabled={currentSlideIndex === totalSlides - 1}
                                    >
                                        <FontAwesomeIcon icon={getIcon("arrow-right")}/>
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.expandedView}>
                            <div className={styles.expandedHeader}>
                                <h2>{expandedSlide.action.type} - {expandedSlide.row.date}</h2>
                                <button 
                                    onClick={() => setExpandedSlide(null)} 
                                    className={styles.expandedClose}
                                >
                                    <FontAwesomeIcon icon={getIcon("compress")} />
                                    
                                </button>
                            </div>
                            <div className={styles.expandedBody}>
                                {expandedSlide.action.type === 'list' && <ReportListView action={expandedSlide.action} row={expandedSlide.row}/>}
                                {expandedSlide.action.type === 'view' && <ReportItemView action={expandedSlide.action} row={expandedSlide.row}/>}
                                {expandedSlide.action.type === 'report' && <ReportView action={expandedSlide.action} row={expandedSlide.row}/>}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}